/**
 * Intelligence Dashboard Real-Time Refresh Utilities
 * 
 * This module provides utilities to ensure Intelligence Dashboards
 * reflect real-time CRM data changes.
 * 
 * USAGE:
 * 1. Import the helper in your component/page
 * 2. Call notifyIntelligenceRefresh() after any successful CRUD operation
 * 
 * MODULES MAPPING:
 * - Sales Dashboard: leads, deals, clients
 * - Support Dashboard: tickets
 * - Accounts Dashboard: invoices, payments, quotes
 * - Inventory Dashboard: inventory_items, stock_movements, purchase_orders
 * - HR Dashboard: employees, attendance, leave_requests
 * - FSM Dashboard: jobs, technicians
 */

// Event name for intelligence refresh
export const INTELLIGENCE_REFRESH_EVENT = 'intelligence:refresh';

/**
 * Module to Dashboard mapping
 * Maps CRM modules to the dashboards they affect
 */
const MODULE_DASHBOARD_MAP = {
  // Sales Dashboard
  leads: ['sales', 'overview'],
  deals: ['sales', 'overview'],
  clients: ['sales', 'accounts', 'overview'],
  
  // Support Dashboard
  tickets: ['support', 'overview'],
  support_tickets: ['support', 'overview'],
  
  // Accounts Dashboard
  invoices: ['accounts', 'overview'],
  payments: ['accounts', 'overview'],
  quotes: ['accounts', 'overview'],
  
  // Inventory Dashboard
  inventory: ['inventory', 'overview'],
  inventory_items: ['inventory', 'overview'],
  items: ['inventory', 'overview'],
  stock_movements: ['inventory', 'overview'],
  purchase_orders: ['inventory', 'overview'],
  sales_orders: ['inventory', 'accounts', 'overview'],
  
  // HR Dashboard
  employees: ['hr', 'overview'],
  users: ['hr', 'overview'],
  attendance: ['hr', 'overview'],
  leave_requests: ['hr', 'overview'],
  
  // FSM Dashboard
  jobs: ['fsm', 'overview'],
  fsm_jobs: ['fsm', 'overview'],
  technicians: ['fsm', 'hr', 'overview'],
  team_members: ['fsm', 'hr', 'overview']
};

/**
 * Emit intelligence refresh event
 * Call this after any CRM CRUD success
 * 
 * @param {string} module - The CRM module that changed (e.g., 'leads', 'tickets')
 * @param {string} action - The action performed ('create' | 'update' | 'delete')
 * @param {string} recordId - Optional ID of the affected record
 */
export function emitIntelligenceRefresh(module, action = 'update', recordId = null) {
  const affectedDashboards = MODULE_DASHBOARD_MAP[module] || ['overview'];
  
  const event = new CustomEvent(INTELLIGENCE_REFRESH_EVENT, {
    detail: { 
      module, 
      action, 
      recordId, 
      affectedDashboards,
      timestamp: Date.now() 
    }
  });
  
  window.dispatchEvent(event);
  
  console.info(
    `[IntelligenceRefresh] module=${module} action=${action} recordId=${recordId || 'N/A'} affects=${affectedDashboards.join(',')}`
  );
}

/**
 * Higher-order function to wrap API calls with intelligence refresh
 * 
 * @param {Function} apiCall - The API call function
 * @param {string} module - The CRM module name
 * @param {string} action - The action type ('create' | 'update' | 'delete')
 * @returns {Function} Wrapped function that emits refresh event on success
 * 
 * @example
 * const createLeadWithRefresh = withIntelligenceRefresh(
 *   (data) => leadsAPI.create(data),
 *   'leads',
 *   'create'
 * );
 */
export function withIntelligenceRefresh(apiCall, module, action) {
  return async (...args) => {
    const result = await apiCall(...args);
    
    // Extract record ID from result if available
    const recordId = result?.data?.id || result?.data?.data?.id || null;
    
    // Emit refresh event after successful API call
    emitIntelligenceRefresh(module, action, recordId);
    
    return result;
  };
}

/**
 * Convenience function to notify intelligence dashboards after CRUD
 * Use this in your component after a successful API call
 * 
 * @param {string} module - The CRM module that changed
 * @param {string} action - The action ('create' | 'update' | 'delete')
 * @param {string} recordId - Optional record ID
 * 
 * @example
 * // In a component after creating a lead:
 * const handleCreateLead = async (data) => {
 *   const result = await leadsAPI.create(data);
 *   notifyIntelligenceRefresh('leads', 'create', result.data.id);
 *   toast.success('Lead created!');
 * };
 */
export function notifyIntelligenceRefresh(module, action = 'update', recordId = null) {
  emitIntelligenceRefresh(module, action, recordId);
}

/**
 * Hook helper to get affected dashboards for a module
 * 
 * @param {string} module - The CRM module name
 * @returns {string[]} Array of dashboard keys that would be affected
 */
export function getAffectedDashboards(module) {
  return MODULE_DASHBOARD_MAP[module] || ['overview'];
}

export default {
  INTELLIGENCE_REFRESH_EVENT,
  emitIntelligenceRefresh,
  withIntelligenceRefresh,
  notifyIntelligenceRefresh,
  getAffectedDashboards
};
